/**
 * CADINU Verified Deploy: library linking and the runtime comparison (same rules as the V4 MetaMask deploy kit's check-runtime.sh):
 *  - library placeholders are linked to the addresses the user gave, at the offsets the artifact lists;
 *  - immutable slots (only known after the constructor ran) are zeroed on both sides at the artifact's immutableReferences;
 *  - a library's own runtime carries its address at bytes 1..20 (PUSH20 <self>, call protection); that slot is linked to the
 *    deployed address;
 *  - everything else, the CBOR metadata tail included, must be byte-for-byte equal. When the only difference sits inside the
 *    metadata tail, the result still says MISMATCH and explains it (different sources or compiler settings).
 */
import type { LinkRef, Range, ImmutableInfo } from './artifact'

export const ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/

export class LinkError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'LinkError'
  }
}

/** Writes each library address (20 bytes) at every offset the link references list. `addresses` is keyed by "file:Name". */
export function linkBytecode(hex: string, links: LinkRef[], addresses: Map<string, string>): string {
  if (!links.length) return hex
  const chars = hex.split('')
  for (const l of links) {
    const a = addresses.get(l.key)
    if (!a || !ADDRESS_RE.test(a)) throw new LinkError(`No valid address for library ${l.name}.`)
    const body = a.slice(2).toLowerCase()
    for (const r of l.offsets) for (let i = 0; i < 40; i++) chars[r.start * 2 + i] = body[i]
  }
  const out = chars.join('')
  if (!/^[0-9a-f]*$/.test(out)) throw new LinkError('The bytecode still holds an unlinked placeholder after linking.')
  return out
}

/** A library's runtime starts with PUSH20 <zero address> ADDRESS EQ; on chain that slot holds the library's own address. */
export function linkLibrarySelf(runtime: string, deployed: string): string {
  if (/^73[0]{40}3014/.test(runtime) && ADDRESS_RE.test(deployed)) return `73${deployed.slice(2).toLowerCase()}${runtime.slice(42)}`
  return runtime
}

export function maskRanges(hex: string, ranges: Range[]): string {
  if (!ranges.length) return hex
  const chars = hex.split('')
  for (const r of ranges) for (let i = r.start * 2; i < (r.start + r.length) * 2 && i < chars.length; i++) chars[i] = '0'
  return chars.join('')
}

/** Byte range of the CBOR metadata tail (solc appends it and its 2-byte length), or null when the tail does not look like one. */
export function cborTail(hex: string): Range | null {
  const bytes = hex.length / 2
  if (bytes < 4) return null
  const len = parseInt(hex.slice(-4), 16)
  if (!Number.isFinite(len) || len < 2 || len + 2 > bytes || len > 512) return null
  const start = bytes - 2 - len
  const first = parseInt(hex.slice(start * 2, start * 2 + 2), 16)
  // a CBOR map with 1..15 entries (0xa1..0xaf): solc writes { ipfs|bzzr0|bzzr1, solc, experimental? }
  if (first < 0xa1 || first > 0xaf) return null
  return { start, length: len + 2 }
}

export type CompareMethod = 'immutables-masked' | 'no-immutables' | 'unknown-immutables'
export type CompareResult = {
  verdict: 'match' | 'mismatch' | 'no-code' | 'no-expected'
  method: CompareMethod
  maskedSlots: number
  expectedBytes: number
  actualBytes: number
  firstDiff: number | null
  onlyMetadataDiffers: boolean
  libraryAddressLinked: boolean
}

const strip = (h: string) => (h.startsWith('0x') || h.startsWith('0X') ? h.slice(2) : h).toLowerCase()

/**
 * Compares on-chain runtime code with the expected runtime of the artifact.
 * @param expectedLinked expected runtime with libraries already linked (hex, no 0x), or null when the artifact has none
 * @param onChain eth_getCode result
 */
export function compareRuntime(expectedLinked: string | null, onChain: string, immutables: ImmutableInfo, deployed: string): CompareResult {
  const actual = strip(onChain)
  const method: CompareMethod = immutables.status === 'listed' ? 'immutables-masked' : immutables.status === 'none' ? 'no-immutables' : 'unknown-immutables'
  const base = { method, maskedSlots: immutables.ranges.length, actualBytes: actual.length / 2, firstDiff: null, onlyMetadataDiffers: false, libraryAddressLinked: false }
  if (!actual.length) return { ...base, verdict: 'no-code', expectedBytes: expectedLinked ? expectedLinked.length / 2 : 0 }
  if (!expectedLinked) return { ...base, verdict: 'no-expected', expectedBytes: 0 }
  const self = linkLibrarySelf(expectedLinked, deployed)
  const exp = maskRanges(self, immutables.ranges)
  const act = maskRanges(actual, immutables.ranges)
  const libraryAddressLinked = self !== expectedLinked
  const expectedBytes = exp.length / 2
  if (exp === act) return { ...base, verdict: 'match', expectedBytes, libraryAddressLinked }
  let d = 0
  while (d < exp.length && d < act.length && exp[d] === act[d]) d++
  const firstDiff = Math.floor(d / 2)
  let onlyMetadataDiffers = false
  const te = cborTail(exp)
  const ta = cborTail(act)
  if (te && ta && te.start === ta.start && firstDiff >= te.start) onlyMetadataDiffers = true
  return { ...base, verdict: 'mismatch', expectedBytes, firstDiff, onlyMetadataDiffers, libraryAddressLinked }
}
